import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { hideLoader, showLoader } from "./loaderSlice";
import { showToast } from "./toastSlice";
import moment from "moment";

const initialState = {
  list: [],
  data: {},
  metrics: [],
  alerts: [],
  activeAlerts: [],
};

export const getContainerMetricsAction = createAsyncThunk(
  "monitoring/getMetrics",
  async ({ id, start_time, projectSlug }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.monitors}metrics/${id}?start_time=${start_time}&interval=1 minute&project_slug=${projectSlug}`,
        {}
      );

      if (res?.success && res?.data) {
        dispatch(setMetrics(res.data));
        return res.data;
      } else {
        return Promise.reject(res.message || "No data returned");
      }
    } catch (error) {
      console.error("Error in getContainerMetricsAction:", error);
      return Promise.reject("An error occurred.");
    }
  }
);

export const getHeartBeatsAction = createAsyncThunk(
  "monitoring/getHeartBeats",
  async ({ id, start_time }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.monitors}aggregate/${id}?start_time=${start_time}&interval=1 minute`,
        {}
      );
      if (res?.success && res?.data) {
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      return Promise.reject("An error occurred.");
    }
  }
);

export const getMonitoringsAction = createAsyncThunk(
  "monitoring/get",
  async (id, { dispatch }) => {
    try {
      const res = await api(
        "get",
        Constants.END_POINT.monitors + `?id=${id}`,
        {}
      );
      if (res?.success && res?.data) {
        await dispatch(setMonitoringsList(res?.data));
      } else {
        return Promise.resolve(res.message);
      }
    } catch (error) {
      return Promise.reject("An error occurred.");
    } finally {
    }
  }
);

export const getMonitoringAction = createAsyncThunk(
  "monitoring/get",
  async ({ id, activeOrg, navigate, start_time }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.monitors}${id}?start_time=${start_time}`,
        {}
      );
      if (res?.success && res?.data) {
        if (res?.data?.organization_id !== activeOrg?.id) {
          navigate("/monitoring");
        }
        let formatedData = await {
          ...res?.data,
          Heartbeats: res?.data?.Heartbeats?.slice().reverse(),
        };
        dispatch(setMonitoringData(formatedData));
      } else {
        return Promise.reject(res.message);
      }
    } catch (error) {
      return Promise.reject("An error occurred.");
    }
  }
);

export const manageMonitoringAction = createAsyncThunk(
  "monitoring/manage",
  async ({ data, status, id, next }, { dispatch }) => {
    try {
      const res = await api(
        "post",
        Constants.END_POINT.monitors +
          (status ? status + "/" : "") +
          (id ? id : ""),
        data
      );
      if (res?.success && res?.data) {
        dispatch(
          showToast({
            severity: "success",
            summary: `Monitor ${
              status === "pause" ? "paused" : "resumed"
            } successfully`,
          })
        );
        next(res?.data?.id, res?.data?.name);
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: `Failed to ${status} monitor`,
        })
      );
      return Promise.reject("An error occurred.");
    }
  }
);

export const deleteMonitoringAction = createAsyncThunk(
  "monitoring/delete",
  async ({ id, next }, { dispatch }) => {
    try {
      const res = await api(
        "delete",
        Constants.END_POINT.monitors + "delete/" + id
      );
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Monitor deleted successfully",
          })
        );
        next();
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to delete monitor",
        })
      );
      return Promise.reject("An error occurred.");
    }
  }
);

export const createAlertAction = createAsyncThunk(
  "monitoring/createAlert",
  async ({ monitorId, data }, { dispatch }) => {
    try {
      const res = await api(
        "post",
        `${Constants.END_POINT.monitors}${monitorId}/alerts`,
        data
      );
      if (res?.success) {
        return res.data;
      } else {
        return Promise.reject(res.message);
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const updateAlertAction = createAsyncThunk(
  "monitoring/updateAlert",
  async ({ alertId, data }, { dispatch }) => {
    try {
      const res = await api(
        "put",
        `${Constants.END_POINT.monitors}alerts/${alertId}`,
        data
      );
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Alert updated successfully",
          })
        );
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to update alert",
        })
      );
      return Promise.reject(error);
    }
  }
);

export const deleteAlertAction = createAsyncThunk(
  "monitoring/deleteAlert",
  async (alertId, { dispatch }) => {
    try {
      const res = await api(
        "delete",
        `${Constants.END_POINT.monitors}alerts/${alertId}`
      );
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Alert deleted successfully",
          })
        );
        return alertId;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to delete alert",
        })
      );
      return Promise.reject(error);
    }
  }
);

export const getAlertsAction = createAsyncThunk(
  "monitoring/getAlerts",
  async (monitorId, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.monitors}${monitorId}/alerts`
      );
      if (res?.success) {
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to fetch alerts",
        })
      );
      return Promise.reject(error);
    }
  }
);

export const getActiveAlertsAction = createAsyncThunk(
  "monitoring/getActiveAlerts",
  async (monitorId, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.monitors}${monitorId}/alerts/active`
      );
      if (res?.success) {
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Failed to fetch active alerts",
        })
      );
      return Promise.reject(error);
    }
  }
);

const monitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    setMonitoringsList: (state, action) => {
      state.list = action.payload;
    },
    setMonitoringData: (state, action) => {
      state.data = action.payload;
    },
    setMetrics: (state, action) => {
      state.metrics = action.payload;
    },
    setAlerts: (state, action) => {
      state.alerts = action.payload;
    },
    setActiveAlerts: (state, action) => {
      state.activeAlerts = action.payload;
    },
    addAlert: (state, action) => {
      state.alerts.push(action.payload);
    },
    updateAlert: (state, action) => {
      const index = state.alerts.findIndex(
        (alert) => alert.id === action.payload.id
      );
      if (index !== -1) {
        state.alerts[index] = action.payload;
      }
    },
    removeAlert: (state, action) => {
      state.alerts = state.alerts.filter(
        (alert) => alert.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAlertAction.fulfilled, (state, action) => {
        state.alerts.push(action.payload);
      })
      .addCase(updateAlertAction.fulfilled, (state, action) => {
        const index = state.alerts.findIndex(
          (alert) => alert.id === action.payload.id
        );
        if (index !== -1) {
          state.alerts[index] = action.payload;
        }
      })
      .addCase(deleteAlertAction.fulfilled, (state, action) => {
        state.alerts = state.alerts.filter(
          (alert) => alert.id !== action.payload
        );
      })
      .addCase(getAlertsAction.fulfilled, (state, action) => {
        state.alerts = action.payload;
      })
      .addCase(getActiveAlertsAction.fulfilled, (state, action) => {
        state.activeAlerts = action.payload;
      });
  },
});

export const {
  setMonitoringsList,
  setMonitoringData,
  setMetrics,
  setAlerts,
  setActiveAlerts,
  addAlert,
  updateAlert,
  removeAlert,
} = monitoringSlice.actions;

export default monitoringSlice.reducer;
